// panelvar.ts — TypeScript mirror of ConstructiVision's 21 panelvar groups
// Reference: docs/source/modernization-2026/31-comprehensive-workflow-analysis.md §2.2
// Each interface maps exactly to the corresponding DCL dialog and LSP module.

// ── UI shared types ───────────────────────────────────────────────────────────
export type ViewPreset =
  | 'front' | 'back' | 'left' | 'right' | 'top' | 'bottom'
  | 'br-iso' | 'bl-iso' | 'fr-iso' | 'fl-iso';

export type ShadingMode = 'wireframe' | 'hidden' | 'shaded';

export type EditMode = 'project' | 'building' | 'elevations' | 'panel' | 'brace' | 'cast';

export type AppView = 'project' | 'building' | 'elevations-2d' | 'elevations-3d' | 'panel-2d' | 'panel-3d';

// ── Corner conditions at elevation ends ──────────────────────────────────────
// Reference: docs/source/tiltup design details/corner-conditions.md
export type CornerType =
  | 'continuous'   // Wall continues past corner (0" end joint)
  | 'butt-int'     // Terminating panel butts interior face of perpendicular wall (3/4")
  | 'butt-ext'     // Terminating panel butts exterior face (3/4")
  | 'miter'        // Both panels meet at 45° (3/8" joint)
  | 'pilaster'     // Panel terminates at poured column (3/4")
  | 'l-panel'      // L-shaped panel wraps corner (0" — monolithic)
  | 'gap';         // Intentional separation at corner (3/4")

export const CORNER_TYPE_LABELS: Record<CornerType, string> = {
  'continuous': 'Continuous',
  'butt-int':   'Terminating(I)',
  'butt-ext':   'Terminating(E)',
  'miter':      'Miter',
  'pilaster':   'Pilaster',
  'l-panel':    'L-Panel',
  'gap':        'Gap',
};

/** Return the effective joint width (inches) for a corner type.
 *  `projectJointWidth` is the standard inter-panel joint (default 0.75"). */
export function cornerJointWidth(type: CornerType, projectJointWidth: number): number {
  if (type === 'continuous' || type === 'l-panel') return 0;
  if (type === 'miter') return 0.375;
  return projectJointWidth;           // butt-int, butt-ext, pilaster, gap
}

/** Direction-based default corner types for new elevations */
export function defaultCorners(direction: string): { leftCorner: CornerType; rightCorner: CornerType } {
  if (direction === 'B' || direction === 'F') return { leftCorner: 'continuous', rightCorner: 'continuous' };
  return { leftCorner: 'butt-int', rightCorner: 'butt-int' };
}

/** Turn angle (degrees, clockwise when viewed from above) for a corner type.
 *  continuous = 0° (inline / expansion joint), everything else = 90° for now.
 *  Future: miter could support 45° or arbitrary angles. */
export function cornerTurnAngle(type: CornerType): number {
  if (type === 'continuous') return 0;
  return 90;   // butt-int, butt-ext, miter, pilaster, l-panel, gap
}

/** Given a corner type set on one side, return the correct type for the
 *  connected neighbor's mirror side.  A butt corner always pairs one
 *  continuous side with one terminating side — never two of the same.
 *  Symmetric conditions (miter, pilaster, l-panel, gap) mirror identically. */
export function complementCornerType(
  type: CornerType,
  existingMirror: CornerType,
): CornerType {
  // Symmetric — both sides share the same type
  if (type === 'miter' || type === 'pilaster' || type === 'l-panel' || type === 'gap') {
    return type;
  }
  // Butt joint: one continuous, one terminating
  if (type === 'continuous') {
    // Mirror must be terminating — preserve existing variant if already terminating
    if (existingMirror === 'butt-int' || existingMirror === 'butt-ext') return existingMirror;
    return 'butt-int'; // default
  }
  // Setting to butt-int or butt-ext → mirror must be continuous
  return 'continuous';
}

/** Thickness-offset rule at the pivot point.
 *  'interior'  → terminating panel meets interior (+Z) face  (butt-int)
 *  'exterior'  → terminating panel meets exterior (−Z) face  (butt-ext, pilaster, gap)
 *  'none'      → no offset (continuous, miter, l-panel) */
export type ThicknessOffsetRule = 'interior' | 'exterior' | 'none';

export function cornerThicknessOffset(type: CornerType): ThicknessOffsetRule {
  if (type === 'butt-int')  return 'interior';
  if (type === 'butt-ext')  return 'exterior';
  if (type === 'pilaster' || type === 'gap') return 'exterior';
  return 'none';  // continuous, miter, l-panel
}

// ── Elevation ─ Architectural direction grouping for panels ──────────────────
export interface Elevation {
  id:          string;
  name:        string;          // Display name: "Back", "Front", or custom
  direction:   'B' | 'F' | 'R' | 'L' | string;  // Facing-relative or custom label
  panelIds:    string[];        // Panel IDs assigned to this elevation
  sortOrder:   number;
  leftCorner:  CornerType;      // Corner condition at left end (first panel side)
  rightCorner: CornerType;      // Corner condition at right end (last panel side)
  leftConnectsTo?:  string;     // Elevation ID this left end connects to (undefined = unlinked)
  rightConnectsTo?: string;     // Elevation ID this right end connects to (undefined = unlinked)
  // Optional world-space overrides for standalone (unchained) elevations
  worldX?: number;              // World X position (inches) — used when not in a chain
  worldZ?: number;              // World Z position (inches) — used when not in a chain
  worldRotation?: number;       // Y-axis rotation (radians) — overrides dirAngle for standalone
}

export const MAX_ELEVATIONS = 16;   // 4 default + 12 custom

export function defaultElevations(): Elevation[] {
  const bId = crypto.randomUUID();
  const rId = crypto.randomUUID();
  const fId = crypto.randomUUID();
  const lId = crypto.randomUUID();
  // Default clockwise chain: B → R → F → L → B
  return [
    { id: bId, name: 'Back',  direction: 'B', panelIds: [], sortOrder: 0, leftCorner: 'continuous', rightCorner: 'continuous', leftConnectsTo: lId, rightConnectsTo: rId },
    { id: rId, name: 'Right', direction: 'R', panelIds: [], sortOrder: 1, leftCorner: 'butt-int',   rightCorner: 'butt-int',   leftConnectsTo: bId, rightConnectsTo: fId },
    { id: fId, name: 'Front', direction: 'F', panelIds: [], sortOrder: 2, leftCorner: 'continuous', rightCorner: 'continuous', leftConnectsTo: rId, rightConnectsTo: lId },
    { id: lId, name: 'Left',  direction: 'L', panelIds: [], sortOrder: 3, leftCorner: 'butt-int',   rightCorner: 'butt-int',   leftConnectsTo: fId, rightConnectsTo: bId },
  ];
}

/** Ensure elevation has corner + connection fields (lazy migration for old projects) */
export function migrateElevationCorners(elev: Elevation): boolean {
  let changed = false;
  if (!elev.leftCorner || !elev.rightCorner) {
    const d = defaultCorners(elev.direction);
    if (!elev.leftCorner)  { elev.leftCorner  = d.leftCorner;  changed = true; }
    if (!elev.rightCorner) { elev.rightCorner = d.rightCorner; changed = true; }
  }
  // leftConnectsTo / rightConnectsTo are optional — no migration needed;
  // the store's wireDefaultConnections() handles cross-linking after all elevations are loaded.
  return changed;
}

/** Wire default clockwise connections for the standard B/R/F/L set.
 *  Call after migration when elevations lack connections. */
export function wireDefaultConnections(elevations: Elevation[]): boolean {
  // Only auto-wire if none of the elevations have any connections yet
  if (elevations.some(e => e.leftConnectsTo || e.rightConnectsTo)) return false;
  const byDir: Record<string, Elevation> = {};
  for (const e of elevations) {
    if (['B', 'R', 'F', 'L'].includes(e.direction) && !byDir[e.direction]) {
      byDir[e.direction] = e;
    }
  }
  const b = byDir['B'], r = byDir['R'], f = byDir['F'], l = byDir['L'];
  if (!b || !r || !f || !l) return false;
  // Clockwise chain: B → R → F → L → B
  b.leftConnectsTo = l.id;  b.rightConnectsTo = r.id;
  r.leftConnectsTo = b.id;  r.rightConnectsTo = f.id;
  f.leftConnectsTo = r.id;  f.rightConnectsTo = l.id;
  l.leftConnectsTo = f.id;  l.rightConnectsTo = b.id;
  return true;
}

export type ActiveDialog =
  | 'none'
  | 'project-setup'
  | 'project-load'
  | 'project-settings'
  | 'panel-options'
  | 'panel-dims'
  | 'rough-openings'
  | 'windows'
  | 'man-doors'
  | 'dock-levelers'
  | 'rect-blockouts'
  | 'round-blockouts'
  | 'horiz-features'
  | 'vert-features'
  | 'pilasters'
  | 'lintels'
  | 'ledgers'
  | 'top-profile'
  | 'footing-shapes'
  | 'side-slabs'
  | 'slope-details'
  | 'brace-points'
  | 'pick-points'
  | 'weld-connections'
  | 'change-thickness'
  | 'panel-attributes'
  | 'site-setup'
  | 'site-place'
  | 'site-edit'
  | 'site-dims'
  | 'drawing-setup'
  | 'revision-notes'
  | 'slope-calc'
  | 'materials-list'
  | 'batch-utilities'
  | 'export-panel-dxf'
  | 'export-site-dxf'
  | 'panel-book-pdf'
  | 'import-dxf'
  | 'export-cvp'
  | 'export-cvt'
  | 'export-cvpanel'
  | 'export-cvsite'
  | 'import-cv'
  | 'snapshots'
  | 'about';

// ── mpvar ─ Panel master geometry (mp_dlg.dcl / mp_dlg.lsp) ─────────────────
export interface MpVar {
  projectName:    string;   // Project / job name
  drawingNumber:  string;   // Drawing number / mark
  panelMark:      string;   // Panel mark (P1, P2, …)
  thickness:      number;   // Panel thickness inches (default 7.25)
  width:          number;   // Panel width inches
  height:         number;   // Panel height inches
  bottomElev:     number;   // Bottom of panel elevation
  botElev:        number;   // Alias for bottomElev (UI shorthand)
  topElev:        number;   // Top of panel elevation
  slabThickness:  number;   // Slab-on-grade thickness
  footing:        boolean;  // Has footing
  footingDepth:   number;
  footingWidth:   number;
  revision:       string;   // Revision letter
  date:           string;   // Drawing date ISO
  drawBy:         string;   // Drawn by initials
  checkBy:        string;   // Checked by initials
  // Extended fields
  jobNumber:      string;
  quantity:       number;
  finish:         string;
  color:          string;
  sealer:         string;
  notes:          string;
  roofLine?:      number;   // Roof line height from panel bottom LEFT side (inches); drawn as dashed line on 2D view
  roofLineR?:     number;   // Roof line height from panel bottom RIGHT side (inches); only used when sloped
  roofLineSloped?: boolean; // true = sloped roof (left != right); false/undefined = level
  castingOrientation: 'face-down' | 'face-up'; // Exterior face down in form (standard) or up
  drawingScale:   string;   // User-set arch scale override (e.g., "3/8\" = 1'-0\""); empty = auto-computed
  // Panel top corner geometry
  heightL?:       number;   // Left edge height inches (default: height)
  heightR?:       number;   // Right edge height inches (default: heightL ?? height)
  topAngleL:      number | 'auto'; // Left top corner slope angle degrees (-60..90) or 'auto'
  topAngleR:      number | 'auto'; // Right top corner slope angle degrees (-60..90) or 'auto'
  topAngleLocked: boolean;  // When true, R mirrors L (default: true)
  topRadiusL:     number;   // Left arc fillet radius inches (0 = sharp corner)
  topRadiusR:     number;   // Right arc fillet radius inches (0 = sharp corner)
}

// ── Slot helper ─ used by most multi-slot dialog groups ──────────────────────
export interface Slot<T> {
  enabled: boolean;
  data:    T;
}

// ── rovar ─ Rough openings (4 slots, ro_dlg.dcl) ────────────────────────────
export interface RoData {
  mark:   string;
  x:      number;  // Center X from left edge
  y:      number;  // Center Y from bottom
  width:  number;
  height: number;
}
export type RoVar = Slot<RoData>[];

// ── wdvar ─ Standard openings / windows (6 slots, wd_dlg.dcl) ───────────────
export interface WdData {
  mark:      string;
  type:      'fixed' | 'slider' | 'awning' | 'custom';
  x:         number;
  y:         number;
  width:     number;
  height:    number;
  sillHeight: number;
  sill:       number;  // Alias for sillHeight
  headHeight: number;
  jamb:      number;  // Jamb reveal depth
}
export type WdVar = Slot<WdData>[];

// ── drvar ─ Man doors (6 slots, dr_dlg.dcl) ─────────────────────────────────
export interface DrData {
  mark:      string;
  type:      'single' | 'double' | 'overhead';
  x:         number;
  y:         number;
  width:     number;
  height:    number;
  threshold: number;
  jamb:      number;
}
export type DrVar = Slot<DrData>[];

// ── dlvar ─ Dock levelers (3 slots, dl_dlg.dcl) ─────────────────────────────
export interface DlData {
  mark:   string;
  x:      number;
  y:      number;
  width:  number;
  height: number;
  depth:  number;  // Pit depth
}
export type DlVar = Slot<DlData>[];

// ── sbvar ─ Rectangular blockouts (6 slots, sb_dlg.dcl) ─────────────────────
export interface SbData {
  mark:   string;
  x:      number;
  y:      number;
  width:  number;
  height: number;
  depth:  number;  // Through or partial
}
export type SbVar = Slot<SbData>[];

// ── rbvar ─ Round blockouts (6 slots, rb_dlg.dcl) ───────────────────────────
export interface RbData {
  mark:     string;
  x:        number;
  y:        number;
  diameter: number;
  depth:    number;
}
export type RbVar = Slot<RbData>[];

// ── fhvar ─ Horizontal feature strips (19 slots / 3 pages, fh_dlg.dcl) ──────
export interface FhData {
  mark:      string;
  type:      'reveal' | 'chamfer' | 'rustication' | 'custom';
  y:         number;   // Bottom Y of strip
  width:     number;   // Horizontal extent (full-width = panel width)
  height:    number;   // Strip height
  depth:     number;   // Groove/reveal depth
  xStart:    number;
  xEnd:      number;
}
export type FhVar = Slot<FhData>[];  // 19 slots

// ── fvvar ─ Vertical feature strips (19 slots / 3 pages, fv_dlg.dcl) ────────
export interface FvData {
  mark:   string;
  type:   'reveal' | 'chamfer' | 'rustication' | 'custom';
  x:      number;   // Center X
  width:  number;
  depth:  number;
  yStart: number;
  yEnd:   number;
}
export type FvVar = Slot<FvData>[];  // 19 slots

// ── plvar ─ Pilasters (2 slots, pl_dlg.dcl) ─────────────────────────────────
export interface PlData {
  mark:       string;
  side:       'left' | 'right';
  width:      number;
  projection: number;  // Inches proud of panel face
  height:     number;  // Full height or partial
}
export type PlVar = Slot<PlData>[];

// ── llvar ─ Lintels (2 slots, ll_dlg.dcl) ───────────────────────────────────
export interface LlData {
  mark:       string;
  overOpening: string;  // References an opening mark
  depth:      number;
  height:     number;
  projection: number;
}
export type LlVar = Slot<LlData>[];

// ── tpvar ─ Top plate / greenplate (tp_dlg.dcl) ──────────────────────────────
export interface TpVar {
  enabled:   boolean;
  thickness: number;
  width:     number;
  material:  'steel' | 'aluminum' | 'other';
  mark:      string;
}

// ── lbvar ─ Ledger bar (lb_dlg.dcl) ─────────────────────────────────────────
export interface LbVar {
  enabled:  boolean;
  y:        number;   // Height from bottom
  depth:    number;
  width:    number;
  mark:     string;
}

// ── chvar ─ Chamfer (ch_dlg.dcl) ─────────────────────────────────────────────
export interface ChVar {
  top:    number;   // Chamfer size top edge (0 = none)
  bottom: number;
  left:   number;
  right:  number;
  reveal: number;   // Panel-edge reveal depth
}

// ── tsvar ─ Top steps (2 slots, ts_dlg.dcl) ─────────────────────────────────
export interface TsData {
  mark:   string;
  x:      number;
  width:  number;
  height: number;
  depth:  number;
}
export type TsVar = Slot<TsData>[];

// ── fsvar ─ Footing steps (2 slots, fs_dlg.dcl) ─────────────────────────────
export interface FsData {
  mark:   string;
  x:      number;
  width:  number;
  height: number;
}
export type FsVar = Slot<FsData>[];

// ── ssvar ─ Spandrel seats (2 slots, ss_dlg.dcl) ────────────────────────────
export interface SsData {
  mark:      string;
  side:      'left' | 'right';
  x:         number;
  width:     number;
  height:    number;
  depth:     number;
  elevation: number;
}
export type SsVar = Slot<SsData>[];

// ── sdvar ─ Slab dowels (4 slots, sd_dlg.dcl) ───────────────────────────────
export interface SdData {
  mark:     string;
  x:        number;
  y:        number;
  size:     string;  // Bar size e.g. "#5"
  spacing:  number;
  embedLen: number;
  projLen:  number;
}
export type SdVar = Slot<SdData>[];

// ── bpvar ─ Brace points (8 slots, bp_dlg.dcl) ───────────────────────────────
export interface BpData {
  mark:     string;
  x:        number;
  y:        number;
  type:     'strongback' | 'angle' | 'other';
  embedLen: number;
  size:     number;  // Insert diameter (inches)
}
export type BpVar = Slot<BpData>[];  // 8 slots

// ── ppvar ─ Pick points (8 slots, pp_dlg.dcl) ────────────────────────────────
export interface PpData {
  mark:       string;
  x:          number;
  y:          number;
  embedLen:   number;
  capacity:   number;  // Tons
  size:       number;  // Insert diameter (inches)
  load:       number;  // Design load (kips)
}
export type PpVar = Slot<PpData>[];  // 8 slots

// ── wcvar ─ Weld connections (15 slots / 5 pages, wc_dlg.dcl) ───────────────
export interface WcData {
  mark:     string;
  sideL:    boolean;   // Left edge of panel
  sideR:    boolean;   // Right edge of panel
  x:        number;    // Custom X pos (only when both sideL+sideR unchecked)
  y:        number;
  wcType:   string;    // Connection type display e.g. '4A', '5A', 'M1'
  structId: string;    // Structural drawing ID e.g. 'A S6.1'
  fabId:    string;    // Fabrication ID (optional) e.g. '4A'
  type:     'plate' | 'angle' | 'embed' | 'other';
  face:     'front' | 'back' | 'top' | 'bottom' | 'left' | 'right';
  plateW:   number;
  plateH:   number;
  anchors:  number;  // Number of headed studs
  notes:    string;
  // Dialog shorthand aliases
  width:    number;
  height:   number;
  thick:    number;
  studs:    number;
  studDia:  number;
}
export type WcVar = Slot<WcData>[];  // 15 slots

// ── Composite PanelData ───────────────────────────────────────────────────────
export interface PanelData {
  id:     string;   // UUID
  mpvar:  MpVar;
  rovar:  RoVar;
  wdvar:  WdVar;
  drvar:  DrVar;
  dlvar:  DlVar;
  sbvar:  SbVar;
  rbvar:  RbVar;
  fhvar:  FhVar;
  fvvar:  FvVar;
  plvar:  PlVar;
  llvar:  LlVar;
  tpvar:  TpVar;
  lbvar:  LbVar;
  chvar:  ChVar;
  tsvar:  TsVar;
  fsvar:  FsVar;
  ssvar:  SsVar;
  sdvar:  SdVar;
  bpvar:  BpVar;
  ppvar:  PpVar;
  wcvar:  WcVar;
  createdAt:  string;
  updatedAt:  string;
}

// ── Site data (site_dlg equivalent) ─────────────────────────────────────────
export interface SitePanelPlacement {
  panelId:  string;
  x:        number;  // Center X on site plan (ft)
  y:        number;  // Center Y on site plan (ft)
  rotation: number;  // Degrees
  angle:    number;  // Alias for rotation
}

// ── Cast-layout view (cv-attach "dumb dump" rectangle around building) ──────
// One INSERT per panel + one CV-ATTACH-MARK per panel, in the cast-out layout
// that cv-cad's cv-attach produces. Coordinates are inches, rotation is radians.
export interface CastLayoutInsert {
  name:     string;   // Block name = panel mark prefix + number, e.g. "TECT001"
  x:        number;
  y:        number;
  rotation: number;
  xscale:   number;
  yscale:   number;
  layer:    string;
}

export interface CastLayoutMark {
  text:     string;   // Mark text like "P1"
  x:        number;
  y:        number;
  rotation: number;
}

export interface CastLayoutData {
  present:     boolean;
  dwgName:     string;
  source:      string;   // e.g. "cv-attach-dumb-dump"
  prefix:      string;   // Panel-mark prefix, e.g. "TECT"
  insertCount: number;
  markCount:   number;
  inserts:     CastLayoutInsert[];
  marks:       CastLayoutMark[];
}

export interface SiteData {
  id:           string;
  projectName:  string;
  drawingNumber: string;
  scale:        number;
  gridSpacing:  number;
  northArrow:   number;  // North arrow angle (degrees)
  panels:       SitePanelPlacement[];
  createdAt:    string;
  updatedAt:    string;
  // ── Dual-view extension (CV-800, CV-751 Stage 3) ──────────────────────────
  // Cast-layout view (`lo`) alongside the erected top-up view (`tu`). When
  // present, the UI offers a TU/LO toggle. Absent ⇒ legacy single-view site.
  layout?:      CastLayoutData;
  activeView?:  'tu' | 'lo';
  // Round-trip preservation. When the site was imported from a wrapped
  // dual-view envelope (cv-cad cvxpsite + cv-attach), we keep the original
  // wrapper opaquely so re-export reproduces the same JSON shape. Internal
  // field — UI code should ignore it.
  _cadEnvelope?: {
    dwgName?:   string;
    nodKey?:    string;
    siteCount?: number;
    nodEntity?: unknown;
    payload?:   unknown;
  };
}

// ── Plans data model (roof plans, floor plans, sections, types) ──────────────

export type RoofElementType = 'dome' | 'gable' | 'flat' | 'barrel-vault';

// Roof framing types — open-web steel joists (OWSJ) per SJI, wood trusses, or steel beams
export type RoofFramingType = 'owsj' | 'wood-truss' | 'steel-beam';

// Ledger connection per ACI 318 Ch.17 / ACI 551.2R for tilt-up
export interface LedgerConnection {
  angleSize:       string;   // e.g. "L4x4x3/8", "L6x4x3/8"
  anchorBoltDia:   number;   // inches (e.g. 0.75 for 3/4")
  anchorBoltSpacing: number; // inches o.c. (24–48 typical)
  embedDepth:      number;   // inches (per ACI 318-19 §17.4.2)
}

// Roof structural data — auto-computed per ACI / SJI standards
export interface RoofStructure {
  framingType:     RoofFramingType;
  framingSpacing:  number;   // inches o.c. (48–96 typical for OWSJ)
  framingDepth:    number;   // inches (joist/truss depth)
  span:            number;   // inches (clear span between bearing walls)
  spanDirection:   'short' | 'long'; // joists span the short dimension
  deckType:        string;   // e.g. "1.5B22" (1.5" type B, 22 ga)
  ledger:          LedgerConnection;
}

export interface RoofElement {
  id:        string;
  type:      RoofElementType;
  name:      string;          // e.g. "Main Dome", "North Barrel Vault"
  x:         number;          // Center X on plan (inches)
  y:         number;          // Center Y on plan (inches)
  // Dome: diameter → circle in 2D, half-sphere in 3D
  diameter?: number;          // inches (dome, barrel-vault width)
  // Barrel vault: length along ridge
  length?:   number;          // inches
  // Gable: width, ridge height above wall top
  width?:    number;          // inches
  ridgeHeight?: number;       // inches above wall top
  rotation?: number;          // degrees
}

export interface DrawingSheet {
  id:       string;
  name:     string;           // "Overall Building Plan", "Floor Plan 1", etc.
  scale:    number;           // Drawing scale factor
  notes:    string;
  /** When false, the sheet uses a frozen footprint instead of auto-recomputing from panels. Default true. */
  autoGenerate?:    boolean;
  /** Captured polygon vertices (inches) used when autoGenerate is false. */
  frozenFootprint?: { x: number; y: number }[];
}

export interface WallSection {
  id:       string;
  label:    string;           // "A", "B", … "Z"
  elevationId?: string;       // Links to an Elevation
  notes:    string;
}

export interface FootingSection {
  id:       string;
  label:    string;           // "1", "2", …
  notes:    string;
}

export interface DoorType {
  id:       string;
  label:    string;           // "Type 1", "Type 2", …
  width:    number;           // inches
  height:   number;           // inches
  notes:    string;
}

export interface WindowType {
  id:       string;
  label:    string;
  width:    number;
  height:   number;
  notes:    string;
}

export interface RoofPlan {
  id:       string;
  name:     string;           // "Roof Plan", "Roof Plan - Wing A"
  elements: RoofElement[];
  notes:    string;
  // Auto-generated from footprint (inset by wall thickness for ledger bearing)
  outline?: { x: number; y: number }[];
  structuralData?: RoofStructure;
  /** When false, the roof outline is frozen (manually edited / no auto-recompute). Default true. */
  autoGenerate?: boolean;
}

export interface PlansData {
  overallPlan:     DrawingSheet | null;
  floorPlans:      DrawingSheet[];
  roofPlans:       RoofPlan[];
  foundationPlan:  DrawingSheet | null;
  wallSections:    WallSection[];
  footingSections: FootingSection[];
  doorTypes:       DoorType[];
  windowTypes:     WindowType[];
}

export function defaultPlansData(): PlansData {
  return {
    overallPlan:     null,
    floorPlans:      [],
    roofPlans:       [],
    foundationPlan:  null,
    wallSections:    [],
    footingSections: [],
    doorTypes:       [],
    windowTypes:     [],
  };
}

// ── Embed type library entry ─────────────────────────────────────────────────
export type EmbedCategory = 'weld' | 'brace' | 'pick';
export interface EmbedTypeEntry {
  id:       string;
  label:    string;
  desc:     string;
  category: EmbedCategory;
  // Partial default data — merged with slot defaults on click
  data:     Partial<WcData> | Partial<BpData> | Partial<PpData>;
}

// ── Project-level structural settings (TCA / ACI 551.2R defaults) ────────────
export interface ProjectSettings {
  units:             string;               // 'imperial' | 'metric'
  titleBlock:        string;               // 'standard' | 'full' | 'none'
  measurementSystem: 'imperial' | 'metric'; // Display measurement system
  precision:         '1/2' | '1/4' | '1/8' | '1/16' | '1/32'; // Fraction precision
  paperSize:         'ARCH-D' | 'ARCH-E' | 'ANSI-D' | 'ANSI-E' | 'A1' | 'A0' | 'Letter' | 'Legal' | 'Tabloid'; // Sheet size
  paperOrientation:  'portrait' | 'landscape'; // Sheet orientation (landscape = default for engineering; portrait for panel drawings)
  concreteStrength:  number;               // f'c psi (default 4000)
  concreteDensity:   number;               // pcf (default 150 normal weight)
  rebarSize:         string;               // Bar designation (default '#5')
  rebarSpacing:      number;               // Inches o.c. both ways (default 18)
  rebarLayers:       'single' | 'double';  // Single or double mat
  clearCover:        number;               // Inches (default 0.75 = 3/4")
  jointWidth:        number;               // Inches between panels (default 0.75)
  maxSnapshots:      number;               // Max auto-snapshots to keep per project (FIFO)
  developerMode:     boolean;              // Show experimental features (concrete, rebar, joints)
  embedTypes?:       EmbedTypeEntry[];     // Project-specific embed type library
}

export function defaultProjectSettings(): ProjectSettings {
  return {
    units:            'imperial',
    titleBlock:       'standard',
    measurementSystem: 'imperial',
    precision:        '1/16',
    paperSize:        'ARCH-D',
    paperOrientation: 'portrait',
    concreteStrength: 4000,
    concreteDensity:  150,
    rebarSize:        '#5',
    rebarSpacing:     18,
    rebarLayers:      'single',
    clearCover:       0.75,
    jointWidth:       0.75,
    maxSnapshots:     20,
    developerMode:   false,
  };
}

// ── Project container ────────────────────────────────────────────────────────
export interface CVProject {
  id:             string;
  name:           string;
  buildingName:   string;
  jobNumber:      string;
  client:         string;
  engineer:       string;
  superintendent: string;
  address?:       string;
  city:           string;
  state:          string;
  phone:          string;
  fax:            string;
  email:          string;
  panels:         PanelData[];
  site:           SiteData | null;
  plans:          PlansData | null;
  elevations:     Elevation[];
  /** Multi-floor support — optional. When undefined the project is single-floor and all elevations belong to one implicit floor. */
  floors?:        Floor[];
  settings:       ProjectSettings;
  lastView?:      AppView;
  /** Persisted ProjectView map framing — center/zoom/layer. Restored on reopen so users can fine-tune the framing and have it stick. */
  mapView?:       MapView;
  _sampleDataVersion?: number;
  createdAt:      string;
  updatedAt:      string;
}

export interface MapView {
  lat:   number;
  lng:   number;
  zoom:  number;
  layer: 'map' | 'satellite';
}

// ── Default factory functions ─────────────────────────────────────────────────
function emptySlot<T>(data: T): Slot<T> {
  return { enabled: false, data };
}

export function defaultSlots<T>(n: number, makeData: () => T): Slot<T>[] {
  return Array.from({ length: n }, () => emptySlot(makeData()));
}

export function defaultMpVar(): MpVar {
  const now = new Date().toISOString().slice(0, 10);
  return {
    projectName: '', drawingNumber: '', panelMark: 'P001',
    thickness: 8.5, width: 359.25, height: 492,             // SegalP15001A: 8½"×29'-11¼"×41'-0"
    bottomElev: 0, botElev: 0, topElev: 41, slabThickness: 5, roofLine: 478.375,  // 39'-10⅜"
    footing: false, footingDepth: 18, footingWidth: 24,
    revision: 'A', date: now, drawBy: '', checkBy: '',
    jobNumber: '', quantity: 1, finish: '', color: '', sealer: '', notes: '',
    castingOrientation: 'face-down', drawingScale: '',
    topAngleL: 'auto', topAngleR: 'auto', topAngleLocked: true,
    topRadiusL: 0, topRadiusR: 0,
  };
}

const emptyRo   = (): RoData   => ({ mark: '', x: 0, y: 0, width: 0, height: 0 });
const emptyWd   = (): WdData   => ({ mark: '', type: 'fixed', x: 0, y: 0, width: 0, height: 0, sillHeight: 0, sill: 0, headHeight: 0, jamb: 0 });
const emptyDr   = (): DrData   => ({ mark: '', type: 'single', x: 0, y: 0, width: 0, height: 0, threshold: 0, jamb: 0 });
const emptyDl   = (): DlData   => ({ mark: '', x: 0, y: 0, width: 0, height: 0, depth: 0 });
const emptySb   = (): SbData   => ({ mark: '', x: 0, y: 0, width: 0, height: 0, depth: 0 });
const emptyRb   = (): RbData   => ({ mark: '', x: 0, y: 0, diameter: 0, depth: 0 });
const emptyFh   = (): FhData   => ({ mark: '', type: 'reveal', y: 0, width: 0, height: 0, depth: 0.75, xStart: 0, xEnd: 0 });
const emptyFv   = (): FvData   => ({ mark: '', type: 'reveal', x: 0, width: 0, depth: 0.75, yStart: 0, yEnd: 0 });
const emptyPl   = (): PlData   => ({ mark: '', side: 'left', width: 0, projection: 0, height: 0 });
const emptyLl   = (): LlData   => ({ mark: '', overOpening: '', depth: 0, height: 0, projection: 0 });
const emptyTs   = (): TsData   => ({ mark: '', x: 0, width: 0, height: 0, depth: 0 });
const emptyFs   = (): FsData   => ({ mark: '', x: 0, width: 0, height: 0 });
const emptySs   = (): SsData   => ({ mark: '', side: 'left', x: 0, width: 0, height: 0, depth: 0, elevation: 0 });
const emptySd   = (): SdData   => ({ mark: '', x: 0, y: 0, size: '#5', spacing: 18, embedLen: 12, projLen: 6 });
const emptyBp   = (): BpData   => ({ mark: '', x: 0, y: 0, type: 'strongback', embedLen: 12, size: 1.25 });
const emptyPp   = (): PpData   => ({ mark: '', x: 0, y: 0, embedLen: 12, capacity: 5, size: 1.25, load: 0 });
const emptyWc   = (): WcData   => ({ mark: '', sideL: true, sideR: true, x: 0, y: 0, wcType: '', structId: '', fabId: '', type: 'plate', face: 'front', plateW: 0, plateH: 0, anchors: 0, notes: '', width: 8, height: 8, thick: 0.375, studs: 4, studDia: 0.75 });

export function defaultPanelData(id: string): PanelData {
  const now = new Date().toISOString();
  return {
    id,
    mpvar: defaultMpVar(),
    rovar: defaultSlots(4, emptyRo) as RoVar,
    wdvar: defaultSlots(6, emptyWd) as WdVar,
    drvar: defaultSlots(6, emptyDr) as DrVar,
    dlvar: defaultSlots(3, emptyDl) as DlVar,
    sbvar: defaultSlots(6, emptySb) as SbVar,
    rbvar: defaultSlots(6, emptyRb) as RbVar,
    fhvar: defaultSlots(19, emptyFh),
    fvvar: defaultSlots(19, emptyFv),
    plvar: defaultSlots(2, emptyPl) as PlVar,
    llvar: defaultSlots(2, emptyLl) as LlVar,
    tpvar: { enabled: false, thickness: 1.5, width: 6, material: 'steel', mark: 'TP-1' },
    lbvar: { enabled: false, y: 0, depth: 0, width: 0, mark: 'LB-1' },
    chvar: { top: 0.75, bottom: 0.75, left: 0.75, right: 0.75, reveal: 0 },
    tsvar: defaultSlots(2, emptyTs) as TsVar,
    fsvar: defaultSlots(2, emptyFs) as FsVar,
    ssvar: defaultSlots(2, emptySs) as SsVar,
    sdvar: defaultSlots(4, emptySd) as SdVar,
    bpvar: defaultSlots(8, emptyBp),
    ppvar: defaultSlots(8, emptyPp),
    wcvar: defaultSlots(15, emptyWc),
    createdAt: now,
    updatedAt: now,
  };
}

// CV-806 — cv-cad fixtures legitimately omit *var groups they don't use
// (e.g. tect001-v1-native.cvpanel ships only mpvar/chvar/wdvar/rovar/drvar).
// Renderers (PanelMesh3D, PanelCanvas2D) and reporters (dxfExport,
// materialsCalc, pdfExport, structuralWarnings, clashDetection) iterate every
// group unconditionally and crash on `.forEach` of undefined. Hydrate any
// missing groups to their empty-slots default while preserving everything the
// importer actually provided. Call at every external-import boundary; never
// from internal mutations where the panel is already well-formed.
export function mergePanelDefaults(panel: PanelData): PanelData {
  // CV-808 — cv-cad TECT templates ship without an `id` key; spreading the
  // source panel last would clobber the default id with null and break every
  // id-keyed lookup (elevations, site placements, React keys). Mint one.
  const id = panel.id || crypto.randomUUID();
  return { ...defaultPanelData(id), ...panel, id };
}

// Hydrate a possibly-sparse project envelope (cv-cad .cvp exports omit
// `settings`, `buildingName`, `client`, etc.) with cv-web defaults so
// downstream consumers (3D viewer reads `project.settings.concreteStrength`)
// never see `undefined`. Mirrors mergePanelDefaults at the project envelope.
export function mergeProjectDefaults(project: Partial<CVProject> & { id?: string }): CVProject {
  const base = defaultProject();
  if (project.id) base.id = project.id;
  return {
    ...base,
    ...project,
    settings: { ...base.settings, ...(project.settings ?? {}) },
  };
}

export function defaultProject(): CVProject {
  const now = new Date().toISOString();
  const id  = crypto.randomUUID();
  return {
    id,
    name: 'New Project',
    buildingName: '',
    jobNumber: '',
    client: '',
    engineer: '',
    superintendent: '',
    city: '',
    state: '',
    phone: '',
    fax: '',
    email: '',
    panels: [],
    site: null,
    plans: null,
    elevations: defaultElevations(),
    settings: defaultProjectSettings(),
    createdAt: now,
    updatedAt: now,
  };
}

// ── Clash detection result types ─────────────────────────────────────────────
export type ClashType = 'panel-panel' | 'feature-feature' | 'feature-edge';
export type ClashSeverity = 'error' | 'warning';

export interface ClashResult {
  type: ClashType;
  severity: ClashSeverity;
  panelIds: string[];
  description: string;
  /** World-space position of the clash center (inches) */
  worldX: number;
  worldY: number;
  worldZ: number;
}

// ── Multi-floor support ──────────────────────────────────────────────────────
export type FloorType = 'standard' | 'mezzanine';

export interface Floor {
  id:             string;
  name:           string;
  type?:          FloorType;             // 'standard' (default) | 'mezzanine'
  elevationIds:   string[];              // Elevations assigned to this floor
  /** Slab thickness above this floor (inches) — adds to next floor's base. Default 5". */
  slabThickness?: number;
  /** For 'mezzanine': explicit Y base elevation (inches) — overrides height-stacking. */
  ledgerHeight?:  number;
}

// ── Brace planning ───────────────────────────────────────────────────────────
export interface BracePlanSettings {
  braceInterior?:  boolean;   // Brace from interior face (default true)
  insertHeightPct: number;    // Brace insert height as % of panel height (typ 60)
  minBraceCount:   number;    // Minimum braces per panel
  maxSpacingIn:    number;    // Max spacing between braces (inches)
  braceAngleDeg:   number;    // Brace angle from vertical (degrees, typ 60)
}

export function defaultBracePlanSettings(): BracePlanSettings {
  return { braceInterior: true, insertHeightPct: 60, minBraceCount: 2, maxSpacingIn: 240, braceAngleDeg: 60 };
}

// ── Cast layout planning ─────────────────────────────────────────────────────
export type CastSortBy = 'pick-order' | 'mark' | 'weight-desc' | 'weight-asc' | 'radius' | 'project';

export interface CastLayoutSettings {
  clearanceIn:        number;    // Panel-to-panel clearance in casting yard
  craneExclusionIn:   number;    // Crane exclusion radius
  craneModel:         string;    // Crane model identifier (e.g. "Grove GMK6300L")
  showCapacityRings:  boolean;   // 3D viewport: render crane capacity rings
  sortBy:             CastSortBy;
  panelGapIn:         number;    // Gap between casting rows (inches)
}

export function defaultCastLayoutSettings(): CastLayoutSettings {
  return { clearanceIn: 36, craneExclusionIn: 480, craneModel: 'Grove GMK6300L', showCapacityRings: true, sortBy: 'pick-order', panelGapIn: 12 };
}

/** Per-panel manual override in the cast yard (drag/rotate). */
export interface PanelOverride {
  cx:           number;
  cy:           number;
  rotationRad:  number;
}

// ── Pick simulation types ────────────────────────────────────────────────────
export interface SerializablePlacedPanel {
  panelId:       string;
  panelMark:     string;
  cx:            number;
  cy:            number;
  installedX:    number;
  installedY:    number;
  pickRadiusIn:  number;
  pickOrder:     number;
  weightTons:    number;
  pickCount:     number;
  pickSpread:    number;
  braceCount:    number;
}

export interface SimMetrics {
  totalArmRotationRad:     number;
  totalArmExtensionDelta:  number;
  mobilizationCount:       number;
  rigChangeCount:          number;
  maxPickRadius:           number;
  totalWeightTons:         number;
  criticalPickCount:       number;
  totalSwingDistIn:        number;
}

export type PickAlgorithmId =
  | 'perimeter-cw'
  | 'nearest-neighbor'
  | 'weight-desc'
  | 'weight-asc'
  | 'mark-sequential'
  | 'random';

export interface SimulationResult {
  algorithmId:  PickAlgorithmId;
  label:        string;
  metrics:      SimMetrics;
  pickOrder:    string[];        // Ordered panel IDs
}

export interface MonteCarloStats {
  min:     SimMetrics;
  p25:     SimMetrics;
  median:  SimMetrics;
  p75:     SimMetrics;
  max:     SimMetrics;
  iqr:     SimMetrics;
}

export interface SimWorkerRequest {
  placed:           SerializablePlacedPanel[];
  craneX:           number;
  craneY:           number;
  craneExclusionIn: number;
  perimOrder:       string[];
  mcIterations:     number;
}

export interface SimWorkerResponse {
  results:     SimulationResult[];
  mcStats:     MonteCarloStats;
  durationMs:  number;
}

// ── Pick animation playback ──────────────────────────────────────────────────
export interface PickStep {
  stepIndex:        number;
  panelId:          string;
  phase:            1 | 2 | 3 | 4 | 5 | 6;   // 1=Hook 2=Tilt 3=Lift 4=Swing 5=Set+brace 6=Release
  craneArmAngleRad: number;
  craneArmLengthIn: number;
  panelCx:          number;
  panelCy:          number;
  panelTiltAngle:   number;
  panelElevFt:      number;
  description:      string;
}

export interface PickAnimState {
  isPlaying:    boolean;
  currentStep:  number;
  speed:        1 | 2 | 4;
  direction:    1 | -1;
}
